/**
 * CatalogSearchModal — Phase 24 / Task 24.3 (DEC-094).
 *
 * A server-backed typeahead picker over the platform catalog (operators / wells)
 * — NOT the client-filtered Combobox, because the wells catalog has ~131k rows
 * and must be searched on the server. The caller passes a `useSearch` hook that
 * returns normalized `{ items, isFetching }`; the modal owns the debounced query
 * and renders the result rows. Minimum 2 characters (matches the API).
 */

import * as React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/components/ui/dialog';

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '40px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
  marginBottom: '10px',
};

const resultRowStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '14px',
  cursor: 'pointer',
  border: 'none',
  borderBottom: '1px solid var(--color-border-secondary)',
  backgroundColor: 'transparent',
  color: 'var(--color-text-primary)',
};

const hintStyle: React.CSSProperties = {
  fontSize: '12px',
  color: 'var(--color-text-tertiary)',
  padding: '8px 4px',
};

/**
 * Debounce a fast-changing value (search-as-you-type) to limit API calls.
 * Exported for the bulk requestor editor, whose location filter is also server-backed —
 * one implementation rather than two that drift.
 */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export interface CatalogSearchModalProps<T> {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly placeholder: string;
  /** A hook returning the normalized search result for the current query. */
  readonly useSearch: (q: string) => { items: readonly T[]; isFetching: boolean };
  readonly itemKey: (item: T) => string;
  readonly renderItem: (item: T) => React.ReactNode;
  readonly onPick: (item: T) => void;
  /** Optional footer slot (e.g. "Not in catalog? Add as customer"). */
  readonly footer?: React.ReactNode;
}

export function CatalogSearchModal<T>({
  open,
  onOpenChange,
  title,
  placeholder,
  useSearch,
  itemKey,
  renderItem,
  onPick,
  footer,
}: CatalogSearchModalProps<T>): React.JSX.Element {
  const [query, setQuery] = React.useState('');
  const debounced = useDebounced(query, 250);
  const { items, isFetching } = useSearch(debounced);
  const tooShort = debounced.trim().length < 2;

  // Reset the query each time the modal opens.
  React.useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <input
          autoFocus
          value={query}
          onChange={(e): void => setQuery(e.target.value)}
          placeholder={placeholder}
          style={searchInputStyle}
          aria-label={placeholder}
        />
        <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
          {tooShort ? (
            <p style={hintStyle}>Type at least 2 characters to search.</p>
          ) : isFetching ? (
            <p style={hintStyle}>Searching…</p>
          ) : items.length === 0 ? (
            <p style={hintStyle}>No matches.</p>
          ) : (
            items.map((item) => (
              <button
                key={itemKey(item)}
                type="button"
                style={resultRowStyle}
                onClick={(): void => onPick(item)}
              >
                {renderItem(item)}
              </button>
            ))
          )}
        </div>
        {footer !== undefined && <div style={{ marginTop: '10px' }}>{footer}</div>}
      </DialogContent>
    </Dialog>
  );
}
